<template>
    <div class="loader" :style="loaderStyle">
        <div v-if="showOrbit" class="loader__orbit" role="status" aria-live="polite">
            <span class="loader__track"></span>
            <span class="loader__ring loader__ring--first"><i></i></span>
            <span class="loader__ring loader__ring--second"><i></i></span>
            <span class="loader__ring loader__ring--third"><i></i></span>
            <span class="loader__label">Загрузка</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

interface Props {
    size?: string | number
}

const props = withDefaults(defineProps<Props>(), { size: '64' })
const showOrbit = ref(false)
let orbitTimer: ReturnType<typeof setTimeout> | undefined

const loaderSize = computed(() => {
    const value = Number(props.size)
    return Number.isFinite(value) && value > 0 ? value : 64
})
const loaderStyle = computed(() => ({
    '--loader-size': `${loaderSize.value}px`,
    '--loader-dot-first': `${Math.max(5, Math.round(loaderSize.value * 0.16))}px`,
    '--loader-dot-second': `${Math.max(4, Math.round(loaderSize.value * 0.125))}px`,
    '--loader-dot-third': `${Math.max(3, Math.round(loaderSize.value * 0.09))}px`
}))

onMounted(() => {
    orbitTimer = setTimeout(() => {
        showOrbit.value = true
    }, 300)
})

onBeforeUnmount(() => {
    if (orbitTimer) clearTimeout(orbitTimer)
})
</script>

<style scoped>
.loader {
    --loader-size: 64px;
    --loader-dot-first: 10px;
    --loader-dot-second: 8px;
    --loader-dot-third: 6px;
    position: relative;
    display: grid;
    width: 100%;
    height: 100%;
    min-height: var(--loader-size);
    place-items: center;
}

.loader__orbit {
    position: absolute;
    width: var(--loader-size);
    height: var(--loader-size);
}

.loader__track,
.loader__ring {
    position: absolute;
    border-radius: 50%;
}

.loader__track {
    inset: 0;
    border: 1px dashed #E7E7E3;
}

.loader__ring {
    animation: loader-orbit 1.4s cubic-bezier(.65, 0, .35, 1) infinite;
}

.loader__ring > i {
    position: absolute;
    top: 0;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
}

.loader__ring--first { inset: 0; }
.loader__ring--first > i { width: var(--loader-dot-first); height: var(--loader-dot-first); background: var(--color-brand-main); }
.loader__ring--second { inset: 12.5%; animation-duration: 1.2s; animation-direction: reverse; }
.loader__ring--second > i { width: var(--loader-dot-second); height: var(--loader-dot-second); background: var(--color-success); }
.loader__ring--third { inset: 26.5%; animation-duration: 1.65s; }
.loader__ring--third > i { width: var(--loader-dot-third); height: var(--loader-dot-third); background: var(--color-moderate); }

.loader__label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
}

@keyframes loader-orbit { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
    .loader__ring { animation: none; }
}
</style>
